import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { v4 as uuidv4 } from 'uuid'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { MessengerUtilityItemList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/messengerUtilityItemList/MessengerUtilityItemList'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { MessageAttachmentType } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Messages'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { getClientFullName } from '../../../../../common/utils/utils'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { useAllClientsData } from '../../../../clients/data/hooks/useAllClientsData'
import { setToastContent } from '../../../../data/ltToastAutoActions'
import { useConversationsData } from '../../../data/hooks/useConversationsData'
import { useDraftsData } from '../../../data/hooks/useDraftsData'
import { useDeleteDraftMutation, useSendMessageMutation } from '../../../data/messagesApi'
import MessengerUtilityItemContainer from '../MessengerUtilityItemContainer'
import MessengerUtilityItemContentContainer from '../MessengerUtilityItemContentContainer'
import MessengerUtilityItemEmptyState from '../MessengerUtilityItemEmptyState'
import MessengerUtilityItemHeader from '../MessengerUtilityItemHeader'

type MessengerUtilityItemDraftsProps = {
  onConversationPressed: ({ clientId, externallySelected }: { clientId: string; externallySelected?: boolean }) => void
}

enum DRAFT_UTILITY_MODALS {
  DELETE_DRAFT = 'deleteDraft',
  SEND_DRAFT = 'sendDraft',
}

const MessengerUtilityItemDrafts: React.FC<MessengerUtilityItemDraftsProps> = ({ onConversationPressed }) => {
  const [shownUtilityItemModal, setShownDraftModal] = useState<DRAFT_UTILITY_MODALS | null>(null)
  const { isPreferredNameEnabled } = useFlags()
  const providerId = useSelector(getAuthUserId)
  const [modalLoading, setModalLoading] = useState(false)
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null)
  const [selectedDraftClientName, setSelectedDraftClientName] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const [deleteDraft] = useDeleteDraftMutation()
  const [createMessage] = useSendMessageMutation()

  const [clients] = useAllClientsData()
  const { data: drafts, isSuccess: draftsFetched } = useDraftsData()
  const { data: conversations } = useConversationsData()
  const draftItems = useMemo(() => {
    if (!draftsFetched) {
      return null
    }
    return drafts
      .filter((draft) =>
        conversations?.find(
          (conversation) =>
            conversation.patient_lyra_id === draft.meta_data?.patient_lyra_id &&
            conversation.conversation_id === draft.conversation_id,
        ),
      )
      .map((draft) => {
        const client = clients.find((client) => client.id === draft.meta_data?.patient_lyra_id)
        const clientId = draft.meta_data?.patient_lyra_id
        const clientName = client ? getClientFullName(client, isPreferredNameEnabled) : ''
        return {
          id: draft.id,
          text: draft.body,
          subText: draft.conversation_id,
          header: clientName,
          isOpen: false,
          onDeletePress: () => {
            track({
              event: mixpanelEvents.DELETE_DRAFTED_MESSAGE_ICON,
              details: {
                clientId: clientId,
              },
            })
            setSelectedDraftId(draft.id)
            setSelectedDraftClientName(clientName)
            setShownDraftModal(DRAFT_UTILITY_MODALS.DELETE_DRAFT)
          },
          onEditPress: () => {
            track({
              event: mixpanelEvents.EDIT_DRAFTED_MESSAGE_ICON,
              details: {
                clientId: clientId,
              },
            })
            clientId && onConversationPressed({ clientId, externallySelected: true })
          },
          onPress: () => {
            track({
              event: mixpanelEvents.EDIT_DRAFTED_MESSAGE_FROM_DRAFT_PREVIEW,
              details: {
                clientId: clientId,
              },
            })
            clientId && onConversationPressed({ clientId, externallySelected: true })
          },
          onSendPress: () => {
            track({
              event: mixpanelEvents.SEND_DRAFTED_MESSAGE_ICON,
              details: {
                clientId: clientId,
              },
            })
            setSelectedDraftId(draft.id)
            setSelectedDraftClientName(clientName)
            setShownDraftModal(DRAFT_UTILITY_MODALS.SEND_DRAFT)
          },
          updateDate: draft.update_date,
          useHoverActionButtons: true,
        }
      })
      ?.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
  }, [clients, conversations, draftsFetched, drafts, isPreferredNameEnabled, onConversationPressed])

  const onDeleteDraft = useCallback(() => {
    const draft = drafts?.find((draft) => draft.id === selectedDraftId)
    if (!draft) {
      return
    }
    setModalLoading(true)
    const conversationId = draft.conversation_id
    const clientId = draft.meta_data?.patient_lyra_id
    track({
      event: mixpanelEvents.DELETE_DRAFTED_MESSAGE_CONFIRM,
      details: {
        clientId: clientId,
      },
    })
    deleteDraft({ conversationId, draftId: draft.id })
      .unwrap()
      .then(() => {
        setShownDraftModal(null)
        dispatch(
          setToastContent({
            text: 'Draft deleted',
            toastType: 'success',
            id: 'messengerDeleteDraftSuccess',
          }),
        )
        setSelectedDraftId(null)
        setSelectedDraftClientName(null)
        setModalLoading(false)
      })
      .catch(() => {
        setShownDraftModal(null)
        dispatch(
          setToastContent({
            text: 'Failed to delete draft',
            toastType: 'error',
            id: 'messengerDeleteDraftFailure',
          }),
        )
        setSelectedDraftId(null)
        setSelectedDraftClientName(null)
        setModalLoading(false)
      })
  }, [deleteDraft, dispatch, drafts, selectedDraftId])

  const onSendDraft = useCallback(() => {
    const draft = drafts?.find((draft) => draft.id === selectedDraftId)
    if (!draft) {
      return
    }
    setModalLoading(true)
    const clientId = draft.meta_data?.patient_lyra_id
    const conversationId = draft.conversation_id
    const metadata: Record<string, any> = {
      conversationInstanceId: uuidv4(),
    }
    if (draft.meta_data?.dbl_attached) {
      metadata.attachments = [
        {
          key: uuidv4(),
          type: MessageAttachmentType.DBL,
        },
      ]
    }
    track({
      event: mixpanelEvents.SEND_DRAFTED_MESSAGE_CONFIRM,
      details: {
        clientId: clientId,
      },
    })
    createMessage({
      conversationId: conversationId,
      data: {
        message_body: draft.body,
        message_type: 'direct',
        message_author_id: providerId,
        message_author_type: 'provider',
        metadata,
        message_draft_id: draft.id,
      },
    })
      .unwrap()
      .then(() => {
        deleteDraft({ conversationId, draftId: draft.id })
          .unwrap()
          .then(() => {
            setShownDraftModal(null)
            setSelectedDraftId(null)
            setSelectedDraftClientName(null)
            setModalLoading(false)
            clientId && onConversationPressed({ clientId, externallySelected: true })
          })
          .catch(() => {
            setShownDraftModal(null)
            dispatch(
              setToastContent({
                text: 'Failed to delete draft',
                toastType: 'error',
                id: 'messengerDeleteDraftFailure',
              }),
            )
            setSelectedDraftId(null)
            setSelectedDraftClientName(null)
            setModalLoading(false)
            clientId && onConversationPressed({ clientId, externallySelected: true })
          })
      })
      .catch(() => {
        setShownDraftModal(null)
        setSelectedDraftId(null)
        setSelectedDraftClientName(null)
        setModalLoading(false)
        dispatch(
          setToastContent({
            text: 'Failed to send draft',
            toastType: 'error',
            id: 'messengerDeleteDraftFailure',
          }),
        )
      })
  }, [createMessage, deleteDraft, dispatch, drafts, onConversationPressed, providerId, selectedDraftId])
  return (
    <MessengerUtilityItemContainer>
      <MessengerUtilityItemHeader>
        <Title size={TitleSize.MEDIUM} text='Drafts' />
      </MessengerUtilityItemHeader>
      <MessengerUtilityItemContentContainer>
        {!draftsFetched ? (
          <LoadingIndicator />
        ) : draftItems?.length === 0 ? (
          <MessengerUtilityItemEmptyState
            text={<FormattedMessage defaultMessage='No drafts available' description='Empty state for drafts page' />}
          />
        ) : (
          <MessengerUtilityItemList items={draftItems} />
        )}
      </MessengerUtilityItemContentContainer>

      <ConfirmationModal
        visible={shownUtilityItemModal == DRAFT_UTILITY_MODALS.DELETE_DRAFT}
        onRequestClose={() => setShownDraftModal(null)}
        cancelButtonText={'Cancel'}
        confirmationButtonText={'Delete'}
        onConfirmationButtonPress={onDeleteDraft}
        modalTitle={'Delete draft'}
        modalContents={
          <>{`Are you sure you want to delete this draft to ${selectedDraftClientName}? This action cannot be undone.`}</>
        }
        confirmButtonLoading={modalLoading}
      />
      <ConfirmationModal
        visible={shownUtilityItemModal == DRAFT_UTILITY_MODALS.SEND_DRAFT}
        onRequestClose={() => setShownDraftModal(null)}
        cancelButtonText={'Cancel'}
        confirmationButtonText={'Send'}
        onConfirmationButtonPress={onSendDraft}
        modalTitle={'Send message'}
        modalContents={<>{`Are you sure you want to send this draft to ${selectedDraftClientName}?`}</>}
        confirmButtonLoading={modalLoading}
      />
    </MessengerUtilityItemContainer>
  )
}

export default MessengerUtilityItemDrafts
